// Maps a concrete Arc write service (the `@aiCallable` service name, which is
// the server class name and what arrives in ATLAS_AI_COMPLETE's
// `executedServices`) to the client domain whose page data must refresh.
//
// This is an ALLOWLIST: external integrations (Asana/Gmail/Calendar) have no
// entry, so they are inherently ignored — no denylist to maintain. Add a row
// here when Arc gains a new internal write worth reflecting live.
//
// ⚠️ The notes services are named `Add`/`Update` (bare class names). They are
// unique across the server's aiCallable registry (which requires uniqueness for
// its own lookups), so keying on them is sound — but the coupling is implicit.
import { OPS_WRITEUP_WORKFLOW_NAME } from '@common/interfaces/opsWriteup'

export type ArcRefetchDomain =
  'notes' | 'commentary' | 'tasks' | 'clientInfo' | 'calendar' | 'projects'

export const ARC_WRITE_REFETCH: Record<string, ArcRefetchDomain> = {
  // notes/{Add,Update}
  Add: 'notes',
  Update: 'notes',
  // generalAnalysis/commentary
  AddAmendmentCommentary: 'commentary',
  AddCommentaryReply: 'commentary',
  UpdateAmendmentCommentary: 'commentary',
  // The OpsWriteup workflow persists Deal Summary commentary as a side effect;
  // surfaced in executedServices by extractExecutedServices (server) so the open
  // Deal Summary tab refetches its commentary just like an amendment write.
  [OPS_WRITEUP_WORKFLOW_NAME]: 'commentary',
  // tasks (Atlas Tasks)
  CreateAtlasTask: 'tasks',
  CreateAtlasTaskComment: 'tasks',
  DeleteAtlasTaskComment: 'tasks',
  UpdateAtlasTask: 'tasks',
  // clients — the open client page's header and narrative
  UpdateClientNarrative: 'clientInfo',
  // Google Calendar: the dashboard's Today card and the Calendar page. A delete runs
  // through an approval, which refreshes the calendars on APPROVAL_RESOLVED instead.
  CreateEvent: 'calendar',
  UpdateEvent: 'calendar',
  DeleteEvent: 'calendar',
  // task projects: the projects list, favourites and an open project's header
  CreateTaskProject: 'projects',
  UpdateTaskProject: 'projects',
  SetTaskProjectFavorite: 'projects',
  DeleteTaskProject: 'projects',
  AddTaskProjectMember: 'projects',
  RemoveTaskProjectMember: 'projects',
  UpdateTaskProjectMemberRole: 'projects',
  // NOTE: notes/Delete + tasks/DeleteAtlasTask are WRITE_DANGEROUS — they run
  // through the approval flow (APPROVAL_RESOLVED), not the immediate-execution
  // path that emits `executedServices`, so a delete refresh is intentionally
  // out of scope here (phase 2). DeleteAtlasTaskComment is NOT one of them: it
  // is WRITE_SAFE and executes immediately, hence its row above.
}

/** An Atlas task service, not a task project one. */
export const isTaskService = (service: string): boolean =>
  /Task/.test(service) && !/TaskProject/.test(service)

/**
 * The domain one finished write refreshes as it lands. Beyond the allowlist, any
 * other task write still re-reads the open task panel it names.
 */
export const arcWriteDomainOf = (service: string): ArcRefetchDomain | null =>
  ARC_WRITE_REFETCH[service] ?? (isTaskService(service) ? 'tasks' : null)
